/**
 * ticketCatalog.ts - 수강권 카탈로그 Firestore 서비스 (instructor-web, read 전용)
 *
 * 경로: users/{ownerUid}/centers/{centerId}/ticketCatalog/{ticketId}
 *
 * 카탈로그 등록(create)은 원장 전용(백엔드 createTicketCatalog, requireAdmin)으로
 * 유지된다. 강사는 이미 등록된 카탈로그를 읽어 회원에게 판매(instructorPurchaseTicket)만
 * 한다. 강사는 firestore rules의 `hasMembership` 조건으로 카탈로그 read 권한을 가진다.
 */

import { collection, getDocs, orderBy, query, type Timestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";

/**
 * 카탈로그 문서 스키마. 백엔드 `createTicketCatalog` payload와 1:1.
 * `createdAt`은 서버 타임스탬프(설정 직후 read하면 null일 수 있음 — UI에서 방어).
 */
export interface TicketCatalogItem {
  id: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
  createdAt?: Timestamp | null;
}

function ticketCatalogCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "ticketCatalog",
  );
}

/**
 * 센터의 모든 수강권 카탈로그를 createdAt 내림차순(최근 등록 우선)으로 반환한다.
 *
 * @param ownerUid - 센터 소유자(원장) UID = activeMembership.centerOwnerUid
 * @param centerId - 센터 ID
 */
export async function getTicketCatalogList(
  ownerUid: string,
  centerId: number,
): Promise<TicketCatalogItem[]> {
  // demo 모드는 백엔드/Firestore에 접근하지 않는다 (기존 firestore 서비스 패턴).
  if (isDemoMode()) return [];
  const col = ticketCatalogCollection(ownerUid, centerId);
  const q = query(col, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as Omit<TicketCatalogItem, "id">;
    return { id: d.id, ...data };
  });
}
